// "Undo this import" (0039): the words for what an undo did, kept apart from the screen so the checks can
// read them (scripts/send-file.test.mjs). Also what the screen makes of "already undone": the last
// answer of an undo can be lost on a weak signal, and the owner's next tap of "Remove them" then meets
// an undo that already finished. That is shown as finished, with the server's own counts, never as an
// error over a form that no longer means anything (final review, 1 Oct 2026).
import { currencySymbol } from './round-config'

export interface UndoResult {
  customers: number
  visits: number
  kept: string[]
  // The undo had already finished before this tap (its answer was lost).
  already: boolean
  // Money the import changed for customers already here, which stays (moneyStaysWords).
  moneyChanges?: MoneyChange[]
}

// A newer backup's figure, or a direct debit it settled (from === to), on a customer already here.
export interface MoneyChange { name: string; from_pence: number; to_pence: number }

// "£12", "£12.50", "£5 credit".
const figure = (p: number): string => {
  const v = Math.abs(p) / 100
  return `${currencySymbol()}${Number.isInteger(v) ? v : v.toFixed(2)}${p < 0 ? ' credit' : ''}`
}

// Undo leaves money an import changed as it is (decision 3 Oct): "This import also changed money for 1 customer (Ken
// Lowe: £12 to £0). Those changes stay. Change a figure on Owed if it was wrong." Empty when it changed none.
export function moneyStaysWords(changes: MoneyChange[] | undefined): string {
  if (!changes?.length) return ''
  const people = [...new Set(changes.map((c) => c.name))]
  const what = changes.slice(0, 6).map((c) => (c.from_pence === c.to_pence ? `${c.name}: a direct debit settled` : `${c.name}: ${figure(c.from_pence)} to ${figure(c.to_pence)}`)).join('; ')
  return `This import also changed money for ${count(people.length, 'customer', 'customers')} (${what}${changes.length > 6 ? '; and more' : ''}). Those changes stay. Change a figure on Owed if it was wrong.`
}

export const count = (n: number, one: string, many: string): string => `${n} ${n === 1 ? one : many}`

// "8 customers and 4 visits", "8 customers", "4 visits", or "" for nothing.
export function whatWords(customers: number, visits: number): string {
  return [customers ? count(customers, 'customer', 'customers') : '', visits ? count(visits, 'visit', 'visits') : ''].filter(Boolean).join(' and ')
}

export function namesWords(names: string[]): string {
  const shown = names.slice(0, 10).join(', ')
  return names.length > 10 ? `${shown} and ${names.length - 10} more` : shown
}

// "Kept 3 customers you've changed since: A, B, C".
export function keptWords(names: string[]): string {
  return `Kept ${count(names.length, 'customer', 'customers')} you've changed since: ${namesWords(names)}`
}

const num = (v: unknown): number => (typeof v === 'number' && Number.isFinite(v) && v >= 0 ? v : 0)

// A refusal's body that says the undo already finished, read as that undo's result; anything else null.
export function alreadyUndone(body: unknown): UndoResult | null {
  if (!body || typeof body !== 'object') return null
  const b = body as Record<string, unknown>
  if (b.code !== 'already_undone') return null
  const removed = (b.removed && typeof b.removed === 'object' ? b.removed : {}) as Record<string, unknown>
  const kept = Array.isArray(b.kept) ? b.kept.filter((k): k is string => typeof k === 'string') : []
  return { customers: num(removed.customers), visits: num(removed.visits), kept, already: true }
}

// The card that replaces the "Remove them?" form, and the toast.
export function undoResultWords(r: UndoResult): { heading: string; lines: string[]; toast: string } {
  const removed = whatWords(r.customers, r.visits)
  const lines: string[] = []
  if (r.already && removed) lines.push(`Removed ${removed}.`)
  if (r.kept.length) lines.push(`${keptWords(r.kept)}.`)
  const stays = moneyStaysWords(r.moneyChanges)
  if (stays) lines.push(stays)
  lines.push('You can import the file again whenever you like.')
  if (r.already) return { heading: 'This import was undone', lines, toast: 'This import was undone.' }
  return { heading: removed ? `Removed ${removed}` : 'Nothing was removed', lines, toast: removed ? `Import undone: ${removed} removed.` : 'Nothing was removed.' }
}
